// src/services/AlgoliaService.js
import { algoliasearch } from 'algoliasearch';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import logger from '../utils/logger';

/**
 * AlgoliaService - Manages Algolia search configuration and operations
 * Implements token-on-demand pattern as described in iOS documentation
 * Using Algolia JavaScript Client v5
 */
class AlgoliaService {
  constructor() {
    // Token cache
    this.algoliaConfig = null;
    this.isRefreshing = false;
    this.algoliaClient = null;
  }

  /**
   * Retrieves Algolia configuration from Firebase Function
   * Implements caching to avoid unnecessary API calls
   * 
   * @returns {Promise<Object>} Configuration object with appId, searchApiKey, indexName, expiresAt
   */
  async getAlgoliaConfig() {
    // Check if we have a valid cached token
    if (this.algoliaConfig && Date.now() < this.algoliaConfig.expiresAt) {
      logger.log('Using cached Algolia token');
      return this.algoliaConfig;
    }

    // If already refreshing, wait a bit and check again
    if (this.isRefreshing) {
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
      if (this.algoliaConfig && Date.now() < this.algoliaConfig.expiresAt) {
        return this.algoliaConfig;
      }
    }

    // Set refreshing flag to prevent concurrent requests
    this.isRefreshing = true;

    try {
      // Call Firebase Function to get fresh token
      const getAppData = httpsCallable(functions, 'getAppData');
      const result = await getAppData();

      // Parse response
      const { algolia } = result.data;

      if (!algolia || !algolia.searchApiKey || !algolia.appId || !algolia.indexName) {
        throw new Error('Invalid response format from Firebase Function');
      }

      // Cache the configuration with expiration (24 hours)
      this.algoliaConfig = {
        appId: algolia.appId,
        searchApiKey: algolia.searchApiKey,
        indexName: algolia.indexName,
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
      };

      logger.sensitive('Successfully retrieved Algolia token');
      return this.algoliaConfig;
    } catch (error) {
      logger.error('Error retrieving Algolia config:', error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Forces a refresh of the Algolia token
   * Clears cache and fetches a new token from Firebase Function
   * 
   * @returns {Promise<Object>} Fresh configuration object
   */
  async forceRefreshAlgoliaConfig() {
    this.algoliaConfig = null; // Clear cache
    this.algoliaClient = null;
    return await this.getAlgoliaConfig();
  }

  /**
   * Initializes Algolia client with current configuration
   * Automatically refreshes token if expired
   * 
   * @returns {Promise<Object>} Algolia client instance
   */
  async initAlgolia() {
    // Get fresh config (will use cache if valid)
    const config = await this.getAlgoliaConfig();

    // Initialize Algolia client using v5 API
    this.algoliaClient = algoliasearch(
      config.appId,
      config.searchApiKey
    );

    return this.algoliaClient;
  }

  /**
   * Performs a search in Algolia with user filtering
   * Automatically handles token refresh on errors
   * 
   * @param {string} query - Search query (minimum 3 characters)
   * @param {string} userId - Firebase user ID (for filtering)
   * @param {number} maxResults - Maximum number of results (default: 30)
   * @returns {Promise<Array>} Array of search results with note details
   */
  async searchNotes(query, userId, maxResults = 30) {
    // Minimum 3 characters validation
    if (!query || query.length < 3) {
      return [];
    }

    // Validate userId
    if (!userId) {
      throw new Error('User ID is required for search');
    }

    try {
      // Initialize Algolia client (will use cached token if valid)
      const client = await this.initAlgolia();
      const config = await this.getAlgoliaConfig();

      // Perform search with user filter using v5 API
      const { results } = await client.search({
        requests: [
          {
            indexName: config.indexName,
            query: query,
            filters: `uid:${userId}`,  // 🔥 CRITICAL: Filter by user ID for security
            hitsPerPage: maxResults
          }
        ]
      });

      const hits = results[0]?.hits || [];
      
      logger.log(`Search completed: ${hits.length} results found for query "${query}"`);

      // Map results to our format
      return hits.map(hit => ({
        noteId: hit.objectID,
        uid: hit.uid,
        emoji: hit.emoji,
        title: hit.title,
        summary: hit.summary,
        summaryShort: hit.summary_short,
        insights: hit.insights,
        isTodoCompleted: hit.isTodoCompleted,
        noteType: hit.noteType || 1, // Default to regular note
        // Include the full hit for any additional fields
        _highlightResult: hit._highlightResult
      }));
    } catch (error) {
      logger.error('Search failed, attempting token refresh:', error);

      // If search fails, try refreshing token and retry once
      try {
        // Force refresh token
        await this.forceRefreshAlgoliaConfig();

        // Re-initialize client with new token
        const client = await this.initAlgolia();
        const config = await this.getAlgoliaConfig();

        // Retry search
        const { results } = await client.search({
          requests: [
            {
              indexName: config.indexName,
              query: query,
              filters: `uid:${userId}`,
              hitsPerPage: maxResults
            }
          ]
        });

        const hits = results[0]?.hits || [];

        logger.log(`Search retry completed: ${hits.length} results found`);

        return hits.map(hit => ({
          noteId: hit.objectID,
          uid: hit.uid,
          emoji: hit.emoji,
          title: hit.title,
          summary: hit.summary,
          summaryShort: hit.summary_short,
          insights: hit.insights,
          isTodoCompleted: hit.isTodoCompleted,
          noteType: hit.noteType || 1,
          _highlightResult: hit._highlightResult
        }));
      } catch (retryError) {
        logger.error('Search failed after token refresh:', retryError);
        throw retryError;
      }
    }
  }

  /**
   * Warm up the Algolia client by pre-initializing and performing a dummy search
   * This ensures the first real search is fast
   * 
   * @param {string} userId - Firebase user ID
   */
  async warmUp(userId) {
    try {
      logger.log('Warming up Algolia search...');
      await this.initAlgolia();
      // Perform a dummy search to warm up
      await this.searchNotes('warmup', userId, 1);
      logger.log('Algolia search warmed up successfully');
    } catch (error) {
      logger.warn('Failed to warm up Algolia search:', error);
      // Non-critical error, just log it
    }
  }
}

// Export singleton instance
export const algoliaService = new AlgoliaService();
export default algoliaService;
